import React, {
  createContext, useState, useContext,
  useEffect, useRef, useCallback
} from 'react';
import TermsModal from '../modals/TermsModal';

const AuthContext = createContext();

const WARNING_DURATION    =  5 * 60 * 1000; // 5 min to respond before auto-logout
const DEFAULT_INACTIVITY_MINUTES = 30;

const getInactivityTimeout = () => {
  const stored = localStorage.getItem('inactivityTimeoutMinutes');
  const minutes = stored ? parseInt(stored, 10) : DEFAULT_INACTIVITY_MINUTES;
  return (isNaN(minutes) || minutes < 1 ? DEFAULT_INACTIVITY_MINUTES : minutes) * 60 * 1000;
};

// Evaluated once per session start; re-read from localStorage on each timer reset
// so changes made in OrgSettings take effect without a full reload.
let INACTIVITY_TIMEOUT = getInactivityTimeout();
const SESSION_DURATION    =  8 * 60 * 60 * 1000;
const REFRESH_BEFORE_EXP  = 60 * 1000;

const WARNING_SECONDS     = WARNING_DURATION / 1000; // 300

const MANAGER_PORTAL_URL  = 'https://www.worldofhopesls.com/manager-portal/';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// ── Timeout modal (self-contained, no external deps) ─────────────────────────
function SessionTimeoutModal({ isOpen, secondsLeft, onStillHere, onLogout }) {
  if (!isOpen) return null;

  const pct   = (secondsLeft / WARNING_SECONDS) * 100;
  const mins  = Math.floor(secondsLeft / 60);
  const secs  = String(secondsLeft % 60).padStart(2, '0');
  const R     = 28;
  const C     = 2 * Math.PI * R;
  const dash  = (pct / 100) * C;

  const ringColor = secondsLeft <= 60
    ? '#ef4444'
    : secondsLeft <= 120
    ? '#f59e0b'
    : '#22c55e';

  const barBg = secondsLeft <= 60
    ? 'linear-gradient(90deg,#ef4444,#f87171)'
    : secondsLeft <= 120
    ? 'linear-gradient(90deg,#f59e0b,#fbbf24)'
    : 'linear-gradient(90deg,#22c55e,#0f4d2e)';

  return (
    <div style={{
      position:       'fixed',
      inset:          0,
      zIndex:         99999,
      background:     'rgba(17,24,40,0.65)',
      backdropFilter: 'blur(3px)',
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'center',
    }}>
      <style>{`
        @keyframes _stm_in {
          from { opacity:0; transform:translateY(10px) scale(0.97) }
          to   { opacity:1; transform:translateY(0)    scale(1)    }
        }
      `}</style>

      <div style={{
        background:    'white',
        borderRadius:  '16px',
        padding:       '2.25rem 2rem',
        width:         '100%',
        maxWidth:      '400px',
        boxShadow:     '0 24px 64px rgba(0,0,0,0.22)',
        display:       'flex',
        flexDirection: 'column',
        alignItems:    'center',
        gap:           '1.25rem',
        animation:     '_stm_in 0.25s ease',
      }}>

        {/* Countdown ring */}
        <div style={{ position:'relative', width:'72px', height:'72px' }}>
          <svg width="72" height="72" style={{ transform:'rotate(-90deg)' }}>
            <circle cx="36" cy="36" r={R} fill="none" stroke="#e5e7eb" strokeWidth="5" />
            <circle
              cx="36" cy="36" r={R}
              fill="none"
              stroke={ringColor}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${dash} ${C}`}
              style={{ transition:'stroke-dasharray 1s linear, stroke 0.4s ease' }}
            />
          </svg>
          <div style={{
            position:       'absolute',
            inset:          0,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            fontSize:       '0.8rem',
            fontWeight:     '700',
            color:          secondsLeft <= 60 ? '#ef4444' : '#111827',
            transition:     'color 0.4s ease',
          }}>
            {mins}:{secs}
          </div>
        </div>

        {/* Copy */}
        <div style={{ textAlign:'center' }}>
          <h2 style={{ margin:'0 0 0.5rem', fontSize:'1.15rem', fontWeight:'700', color:'#111827' }}>
            Still there?
          </h2>
          <p style={{ margin:0, fontSize:'0.875rem', color:'#6b7280', lineHeight:'1.55' }}>
            You've been inactive for 10 minutes. For security, you'll be logged out automatically
            if there's no response.
          </p>
        </div>

        {/* Progress bar */}
        <div style={{
          width:'100%', height:'4px',
          background:'#f3f4f6', borderRadius:'99px', overflow:'hidden',
        }}>
          <div style={{
            height:'100%',
            width:`${pct}%`,
            borderRadius:'99px',
            background: barBg,
            transition:'width 1s linear, background 0.4s ease',
          }} />
        </div>

        {/* Buttons */}
        <div style={{ display:'flex', gap:'0.75rem', width:'100%' }}>
          <button
            onClick={onLogout}
            style={{
              flex:'1', padding:'0.65rem',
              background:'#f3f4f6', color:'#374151',
              border:'none', borderRadius:'8px',
              cursor:'pointer', fontWeight:'600', fontSize:'0.875rem',
            }}
            onMouseEnter={e => e.currentTarget.style.background='#e5e7eb'}
            onMouseLeave={e => e.currentTarget.style.background='#f3f4f6'}
          >
            Log out
          </button>
          <button
            onClick={onStillHere}
            style={{
              flex:'2', padding:'0.65rem',
              background:'linear-gradient(135deg,#22c55e 0%,#0f4d2e 100%)',
              color:'white', border:'none', borderRadius:'8px',
              cursor:'pointer', fontWeight:'600', fontSize:'0.875rem',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity='0.88'}
            onMouseLeave={e => e.currentTarget.style.opacity='1'}
          >
            Yes, keep me logged in
          </button>
        </div>
      </div>
    </div>
  );
}

// ── AuthProvider ──────────────────────────────────────────────────────────────
export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const [showWarning,    setShowWarning]    = useState(false);
  const [secondsLeft,    setSecondsLeft]    = useState(WARNING_SECONDS);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const showWarningRef = useRef(false);

  const refreshTimerRef    = useRef(null);
  const inactivityTimerRef = useRef(null);
  const sessionTimerRef    = useRef(null);
  const warningTimerRef    = useRef(null);
  const countdownRef       = useRef(null);
  const logoutRef          = useRef(null);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async (reason = '') => {
    if (reason) console.log(`Logging out: ${reason}`);

    // Capture role BEFORE clearing localStorage so we know where to redirect
    const storedUser = localStorage.getItem('user');
    const userData   = storedUser ? JSON.parse(storedUser) : null;
    const userRole   = userData?.role;
    const token      = userData?.token;

    if (token) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {}
    }

    clearTimeout(refreshTimerRef.current);
    clearTimeout(inactivityTimerRef.current);
    clearTimeout(sessionTimerRef.current);
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownRef.current);

    showWarningRef.current = false;
    setShowWarning(false);
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('sessionStart');
    localStorage.removeItem('lastActivity');

    // Managers came from WorldOfHope — send them back there on logout
    if (userRole === 'manager') {
      window.location.href = MANAGER_PORTAL_URL;
    }
  }, []);

  useEffect(() => { logoutRef.current = logout; }, [logout]);

  // ── Stop the warning modal + its timers ───────────────────────────────────
  const clearWarning = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownRef.current);
    showWarningRef.current = false;
    setShowWarning(false);
    setSecondsLeft(WARNING_SECONDS);
  }, []);

  // ── Show the warning modal + start auto-logout countdown ─────────────────
  const triggerWarning = useCallback(() => {
    showWarningRef.current = true;
    setShowWarning(true);
    setSecondsLeft(WARNING_SECONDS);

    countdownRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(countdownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);

    warningTimerRef.current = setTimeout(() => {
      logoutRef.current?.('inactivity warning expired');
    }, WARNING_DURATION);
  }, []);

  // ── Inactivity timer ──────────────────────────────────────────────────────
  const resetInactivityTimer = useCallback(() => {
    if (showWarningRef.current) return;

    // Re-read every reset so OrgSettings changes apply without a reload
    INACTIVITY_TIMEOUT = getInactivityTimeout();

    clearTimeout(inactivityTimerRef.current);
    localStorage.setItem('lastActivity', Date.now().toString());

    inactivityTimerRef.current = setTimeout(() => {
      triggerWarning();
    }, INACTIVITY_TIMEOUT);
  }, [triggerWarning]);

  // ── "Still here" handler ──────────────────────────────────────────────────
  const handleStillHere = useCallback(() => {
    clearWarning();
    resetInactivityTimer();
  }, [clearWarning, resetInactivityTimer]);

  // ── "Log out" handler ─────────────────────────────────────────────────────
  const handleWarningLogout = useCallback(() => {
    clearWarning();
    logoutRef.current?.('user chose to log out from warning');
  }, [clearWarning]);

  // ── Inactivity tracking setup ─────────────────────────────────────────────
  const startInactivityTracking = useCallback(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer));
    const cleanup = () => events.forEach(e => window.removeEventListener(e, resetInactivityTimer));

    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity);
      if (elapsed >= INACTIVITY_TIMEOUT) {
        const storedUser = localStorage.getItem('user');
        // Always add listeners first so "Still Here" dismissal works correctly.
        // Bug was here: returning early meant listeners were never attached, so
        // mouse/keyboard activity couldn't reset the timer after dismissal.
        if (!storedUser) { cleanup(); return () => {}; }
        triggerWarning();
      } else {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = setTimeout(() => {
          triggerWarning();
        }, INACTIVITY_TIMEOUT - elapsed);
      }
    } else {
      resetInactivityTimer();
    }

    return cleanup;
  }, [resetInactivityTimer, triggerWarning]);

  // ── Session timer (hard 8h limit) ─────────────────────────────────────────
  const startSessionTimer = useCallback(() => {
    clearTimeout(sessionTimerRef.current);
    const sessionStart = localStorage.getItem('sessionStart');
    const now = Date.now();

    if (sessionStart) {
      const elapsed   = now - parseInt(sessionStart);
      const remaining = SESSION_DURATION - elapsed;
      if (remaining <= 0) { logout('session expired on load'); return; }
      sessionTimerRef.current = setTimeout(() => {
        logoutRef.current?.('session duration limit reached');
      }, remaining);
    } else {
      localStorage.setItem('sessionStart', now.toString());
      sessionTimerRef.current = setTimeout(() => {
        logoutRef.current?.('session duration limit reached');
      }, SESSION_DURATION);
    }
  }, [logout]);

  // ── Token refresh ─────────────────────────────────────────────────────────
  const refreshAccessToken = useCallback(async () => {
    const sessionStart = localStorage.getItem('sessionStart');
    if (sessionStart && Date.now() - parseInt(sessionStart) >= SESSION_DURATION) {
      logout('session expired during refresh'); return null;
    }

    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST' });
      if (!res.ok) { logout('refresh token rejected'); return null; }

      const data = await res.json();
      setUser(prev => {
        if (!prev) return null;
        const updated = { ...prev, token: data.token };
        localStorage.setItem('user', JSON.stringify(updated));
        return updated;
      });
      return data.token;
    } catch (err) {
      console.error('Token refresh failed:', err);
      logout('refresh network error');
      return null;
    }
  }, [logout]);

  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimerRef.current);
    const sessionStart = localStorage.getItem('sessionStart');
    if (!sessionStart) return;
    const sessionElapsed   = Date.now() - parseInt(sessionStart);
    const sessionRemaining = SESSION_DURATION - sessionElapsed;
    if (sessionRemaining <= 2 * 60 * 1000) return;
    const refreshIn = Math.min(
      15 * 60 * 1000 - REFRESH_BEFORE_EXP,
      sessionRemaining - REFRESH_BEFORE_EXP
    );
    if (refreshIn <= 0) return;
    refreshTimerRef.current = setTimeout(async () => {
      const newToken = await refreshAccessToken();
      if (newToken) scheduleRefresh();
    }, refreshIn);
  }, [refreshAccessToken]);

  // ── Init from localStorage ────────────────────────────────────────────────
  useEffect(() => {
    const storedUser   = localStorage.getItem('user');
    const sessionStart = localStorage.getItem('sessionStart');

    if (storedUser) {
      if (sessionStart && Date.now() - parseInt(sessionStart) >= SESSION_DURATION) {
        logout('session expired on page load');
        setLoading(false);
        return;
      }
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      if (parsed.role === 'superadmin' && !parsed.termsAccepted) {
        setShowTermsModal(true);
      }
      scheduleRefresh();
      startSessionTimer();
      const cleanup = startInactivityTracking();
      setLoading(false);
      return () => {
        cleanup();
        clearTimeout(refreshTimerRef.current);
        clearTimeout(inactivityTimerRef.current);
        clearTimeout(sessionTimerRef.current);
        clearTimeout(warningTimerRef.current);
        clearInterval(countdownRef.current);
      };
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.message || 'Invalid credentials' };
      }
      const userData = {
        id:             data.userId,
        email:          data.email,
        firstName:      data.firstName,
        lastName:       data.lastName,
        role:           data.role,
        organizationId: data.organizationId,
        token:          data.token,
        termsAccepted:  data.termsAccepted ?? false,
      };
      setUser(userData);
      localStorage.setItem('user',         JSON.stringify(userData));
      localStorage.setItem('sessionStart', Date.now().toString());
      localStorage.setItem('lastActivity', Date.now().toString());

      if (data.role === 'superadmin' && !data.termsAccepted) {
        setShowTermsModal(true);
      }

      scheduleRefresh();
      startSessionTimer();
      resetInactivityTimer();
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // ── Accept Terms ──────────────────────────────────────────────────────────
  const acceptTerms = useCallback(async () => {
    try {
      const token = user?.token;
      const res = await fetch(`/api/organizations/${user?.organizationId}/accept-terms`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return { success: false };
      const updatedUser = { ...user, termsAccepted: true };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setShowTermsModal(false);
      return { success: true };
    } catch (err) {
      console.error('Accept terms error:', err);
      return { success: false };
    }
  }, [user]);

  // ── Auth fetch ────────────────────────────────────────────────────────────
  const authFetch = async (url, options = {}) => {
    const token = user?.token;
    const res = await fetch(url, {
      ...options,
      headers: { ...options.headers, Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) {
      const data = await res.json().catch(() => ({}));
      if (data.expired) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return fetch(url, {
            ...options,
            headers: { ...options.headers, Authorization: `Bearer ${newToken}` },
          });
        }
      }
    }
    return res;
  };

  const isEmployee   = () => user?.role === 'employee';
  const isOwner      = () => user?.role === 'superadmin';
  const isSuperAdmin = () => user?.role === 'superadmin' || user?.role === 'admin';
  const isManager    = () => user?.role === 'manager' || isSuperAdmin();

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, authFetch,
      isEmployee, isManager, isSuperAdmin, isOwner,
      acceptTerms,
    }}>
      {children}

      {user && showTermsModal && (
        <TermsModal isOpen={showTermsModal} onAccept={acceptTerms} />
      )}

      {user && (
        <SessionTimeoutModal
          isOpen={showWarning}
          secondsLeft={secondsLeft}
          onStillHere={handleStillHere}
          onLogout={handleWarningLogout}
        />
      )}
    </AuthContext.Provider>
  );
};

// ── ProtectedRoute ────────────────────────────────────────────────────────────
export const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user)   return <div>Please log in to access this page.</div>;
  if (requireSuperAdmin && user.role !== 'superadmin' && user.role !== 'admin')
    return <div>Access Denied. Admin privileges required.</div>;
  if (requireAdmin && user.role !== 'manager' && user.role !== 'superadmin' && user.role !== 'admin')
    return <div>Access Denied. Manager privileges required.</div>;
  return children;
};